import { useState } from 'react';
import { Link } from 'react-router';
import {
  worldToZonePercent,
  zoneBoundsByArea,
  zoneBoundsByName,
  type SpawnLocation,
  type ZoneBounds,
} from '@thottbot/shared';

export interface MarkerGroup {
  label: string;
  /** Link target for the group's markers (the NPC page on a zone map). */
  href?: string;
  locations: (SpawnLocation & { count?: number })[];
}

const MAX_MARKERS = 300;
const CANVAS = { width: 1002, height: 668 };
/** Marker colours cycle per group on multi-NPC maps (zone page). */
const COLOURS = ['#e0242b', '#1a5fd0', '#1a8a1a', '#c47a00', '#7a2ec4', '#0c8a8a'];

interface Marker {
  key: string;
  cx: number;
  cy: number;
  title: string;
  group: number;
  /** Addon sightings behind the marker (1 for a seeded spawn point). */
  count: number;
  counted: boolean;
}

/**
 * World spawns -> deduplicated canvas markers (0.5 % grid), capped; counts the overflow.
 * Spawn points that fall in the same cell add their sighting counts together.
 */
export function buildMarkers(
  bounds: ZoneBounds,
  groups: MarkerGroup[],
): { markers: Marker[]; hidden: number } {
  const seen = new Map<string, Marker>();
  const markers: Marker[] = [];
  let hidden = 0;
  groups.forEach((g, gi) => {
    for (const loc of g.locations) {
      if (loc.map !== bounds.mapId) continue;
      const p = worldToZonePercent(bounds, loc);
      if (p.x < 0 || p.x > 100 || p.y < 0 || p.y > 100) continue;
      const key = `${gi}:${Math.round(p.x * 2)}:${Math.round(p.y * 2)}`;
      const existing = seen.get(key);
      if (existing) {
        if (loc.count !== undefined) {
          existing.count += loc.count;
          existing.counted = true;
        }
        continue;
      }
      if (markers.length >= MAX_MARKERS) {
        hidden++;
        continue;
      }
      const marker: Marker = {
        key,
        cx: (p.x / 100) * CANVAS.width,
        cy: (p.y / 100) * CANVAS.height,
        title: `${g.label} (${p.x}, ${p.y})`,
        group: gi,
        count: loc.count ?? 1,
        counted: loc.count !== undefined,
      };
      seen.set(key, marker);
      markers.push(marker);
    }
  });
  for (const m of markers) {
    if (m.counted) m.title += ` · ${m.count} ${m.count === 1 ? 'sighting' : 'sightings'}`;
  }
  return { markers, hidden };
}

/** Marker radius grows gently with the sighting count (7 px for one, 11 px from 16 up). */
function radius(base: number, count: number): number {
  return base + Math.min(4, Math.log2(Math.max(1, count)));
}

/**
 * A zone map with spawn markers: the imported client map when `mapUrl` is set, otherwise a
 * schematic frame. Renders nothing when the zone has no client-map bounds (instances) or no
 * spawn falls on it, so the Locations table stays the only view in those cases.
 */
export function ZoneMap({
  zone,
  mapUrl,
  groups,
}: {
  zone: { name: string; areaId?: number | null };
  mapUrl: string | null | undefined;
  groups: MarkerGroup[];
}) {
  const [broken, setBroken] = useState(false);
  const bounds =
    (zone.areaId != null ? zoneBoundsByArea(zone.areaId) : undefined) ?? zoneBoundsByName(zone.name);
  if (!bounds) return null;
  const { markers, hidden } = buildMarkers(bounds, groups);
  if (markers.length === 0) return null;
  const art = mapUrl && !broken ? mapUrl : null;
  const multi = groups.length > 1;
  return (
    <figure className="zone-map" data-testid="zone-map">
      <svg
        viewBox={`0 0 ${CANVAS.width} ${CANVAS.height}`}
        role="img"
        aria-label={`${zone.name} map with ${markers.length} spawn ${markers.length === 1 ? 'point' : 'points'}`}
      >
        {art ? (
          <image
            href={art}
            width={CANVAS.width}
            height={CANVAS.height}
            onError={() => setBroken(true)}
          />
        ) : (
          <>
            <rect
              x="1"
              y="1"
              width={CANVAS.width - 2}
              height={CANVAS.height - 2}
              fill="#f4efe2"
              stroke="#8a7a52"
              strokeWidth="2"
            />
            <text x={CANVAS.width / 2} y="40" textAnchor="middle" fontSize="28" fill="#8a7a52">
              {zone.name}
            </text>
          </>
        )}
        {markers.map((m) => {
          const circle = (
            <circle
              key={m.key}
              cx={m.cx}
              cy={m.cy}
              r={radius(multi ? 6 : 7, m.count)}
              data-count={m.count}
              fill={COLOURS[m.group % COLOURS.length]}
              stroke="#fff"
              strokeWidth="2"
            >
              <title>{m.title}</title>
            </circle>
          );
          const href = groups[m.group]?.href;
          return href ? (
            <a key={m.key} href={href}>
              {circle}
            </a>
          ) : (
            circle
          );
        })}
      </svg>
      {(multi || hidden > 0) && (
        <figcaption className="muted">
          {multi &&
            groups.map((g, i) => (
              <span key={g.label} className="zone-map-legend">
                <span
                  className="zone-map-swatch"
                  style={{ background: COLOURS[i % COLOURS.length] }}
                />
                {g.href ? <Link to={g.href}>{g.label}</Link> : g.label}
              </span>
            ))}
          {hidden > 0 && <span>{hidden} more spawn points not shown</span>}
        </figcaption>
      )}
    </figure>
  );
}
